// Data hooks for the *Photo matching* tab. The components under
// `components/faces/` stay pure — every fetch lives here (ADR-0039).
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { STAR_MAX_CANDIDATES } from '@/components/faces/star-layout'
import { retryUnlessGone } from '@/features/entity-resolution/merged-redirect'
import {
  type ConfirmedMerge,
  identityMergeMutation,
} from '@/features/identities/hooks/useMergedInto'
import { ADVANCED_FACE_DETECTION_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import {
  type FaceDecision,
  type FaceMatchingIdentityDetail,
  type FaceMatchingIdentityList,
  faceMatchingApi,
  type IdentityDetailQuery,
} from '@/services/api/face-matching'
import { withoutCandidate, withoutPending, withReference } from '../optimistic'

export const faceMatchingKeys = {
  all: (caseId: string) => ['case', caseId, 'face-matching'] as const,
  identities: (caseId: string) => [...faceMatchingKeys.all(caseId), 'identities'] as const,
  identity: (caseId: string, personId: string, query: IdentityDetailQuery) =>
    [
      ...faceMatchingKeys.all(caseId),
      'identity',
      personId,
      query.suggestedOnly ?? false,
      query.page ?? 1,
      query.pageSize ?? null,
    ] as const,
}

/**
 * Everything the case's face matching is read through, in one call: both
 * identity details (the star's and the table's), the identity list behind the
 * ER tab and the faces card of the identity page all hang under this prefix,
 * so a decision or a reference toggle leaves none of them stale.
 */
export function invalidateFaceMatching(queryClient: QueryClient, caseId: string) {
  void queryClient.invalidateQueries({ queryKey: faceMatchingKeys.all(caseId) })
}

/**
 * The case's reads a merge moves besides face matching, by query-key prefix: the
 * overlay's document faces (`useDocumentFaces`), the identity pickers, the photo
 * grids (`useIdentityPhotos`), entity resolution — groups, pending count and the
 * audit the merge lands in (`useGroupReview`, `useAuditLog`) — and the graph, as
 * `useMergeGroup` settles an ER merge.
 */
const MERGE_SCOPES = [
  'document-faces',
  'identities',
  'identity-photos',
  'entity-resolution',
  'graph',
] as const

/**
 * Everything an identity merge leaves stale (ADR-0042): the merged identity's
 * faces, documents and relations now belong to the survivor, and the merge is an
 * entity-resolution record. The identity page and the identities list read
 * outside the query cache, so their callers re-read them on their own.
 *
 * *mergedId* is the identity the merge folded away. Its own reads are left
 * alone: it is gone, and a view still on it for one more render would only
 * refetch them into a 404 before it follows the survivor.
 */
export function invalidateAfterIdentityMerge(
  queryClient: QueryClient,
  caseId: string,
  mergedId: string | null = null
) {
  const scopes = [
    faceMatchingKeys.all(caseId),
    ...MERGE_SCOPES.map(scope => ['case', caseId, scope] as const),
  ]
  for (const queryKey of scopes) {
    void queryClient.invalidateQueries({
      queryKey,
      predicate: query => !mergedId || !query.queryKey.includes(mergedId),
    })
  }
}

/** The candidate table is a page of face crops — keep pages small. */
export const CANDIDATES_PAGE_SIZE = 25

/**
 * The star reads its own detail query: no filter and always page one, so the
 * table's filter chips and paging can never hide the identity's strongest
 * pending suggestions from the star (SC-007).
 */
export const STAR_DETAIL_OPTIONS = { pageSize: STAR_MAX_CANDIDATES } as const

/**
 * The id a face read goes out with: none while Advanced Face Detection is off
 * (ADR-0051), so the read neither runs nor answers from the cache — every
 * consumer, present and future, goes inert without a gate of its own.
 */
export function useFaceReadId<Id extends string | null>(id: Id): Id | null {
  const { enabled } = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  return enabled ? id : null
}

/** Identity rows with pending suggestions, ordered by their strongest score. */
export function useFaceMatchingIdentities(caseId: string) {
  const readCaseId = useFaceReadId(caseId) ?? ''
  return useQuery({
    queryKey: faceMatchingKeys.identities(readCaseId),
    queryFn: () => faceMatchingApi.listIdentities(readCaseId),
    enabled: Boolean(readCaseId),
    staleTime: 60 * 1000,
    // Matching runs in the background; keep the case rail's alert current.
    refetchInterval: 60 * 1000,
  })
}

/** All unresolved face suggestions in the case, shared by the rail and ER tab. */
export function usePendingFaceMatchCount(caseId: string) {
  const { data } = useFaceMatchingIdentities(caseId)
  return data?.identities.reduce((count, identity) => count + identity.pending_total, 0) ?? 0
}

/** The detail query of one view's state — its query and the mutations share it. */
export function identityQuery(options: IdentityDetailQuery = {}): IdentityDetailQuery {
  return {
    suggestedOnly: options.suggestedOnly ?? false,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? CANDIDATES_PAGE_SIZE,
  }
}

/** One identity's reference strip and one page of its candidates. */
export function useFaceMatchingIdentity(
  caseId: string,
  personId: string | null,
  options: IdentityDetailQuery = {}
) {
  const query = identityQuery(options)
  const readPersonId = useFaceReadId(personId)
  return useQuery({
    queryKey: faceMatchingKeys.identity(caseId, readPersonId ?? '', query),
    queryFn: () => faceMatchingApi.identityDetail(caseId, readPersonId as string, query),
    enabled: Boolean(caseId) && Boolean(readPersonId),
    staleTime: 60 * 1000,
    // Keep the previous page on screen while the next one loads.
    placeholderData: keepPreviousData,
    // An identity that is gone stays gone, and one that was merged away names
    // its survivor (ADR-0053): retrying only delays the view that follows it.
    retry: retryUnlessGone,
  })
}

/** The face a decision, a merge or a toggle is in flight for — its buttons wait. */
export function inFlight(mutation: {
  isPending: boolean
  variables?: { faceId: string }
}): string | null {
  return mutation.isPending ? (mutation.variables?.faceId ?? null) : null
}

type DetailKey = ReturnType<typeof faceMatchingKeys.identity>

/** The cached details a mutation replaced, so a refusal can put them back. */
type DetailSnapshot = (readonly [DetailKey, FaceMatchingIdentityDetail | undefined])[]

/** What a candidate's optimistic removal replaced: its details and the identity list. */
interface CandidateSnapshot {
  previousDetails: DetailSnapshot
  previousList: FaceMatchingIdentityList | undefined
}

function detailKeys(caseId: string, personId: string | null, queries: IdentityDetailQuery[]) {
  return queries.map(query => faceMatchingKeys.identity(caseId, personId ?? '', query))
}

/** Stop the details' fetches — a late answer would undo the edit — and keep them. */
async function snapshotDetails(
  queryClient: QueryClient,
  keys: DetailKey[]
): Promise<DetailSnapshot> {
  await Promise.all(keys.map(key => queryClient.cancelQueries({ queryKey: key })))
  return keys.map(key => [key, queryClient.getQueryData<FaceMatchingIdentityDetail>(key)] as const)
}

function restoreDetails(queryClient: QueryClient, snapshot: DetailSnapshot = []) {
  for (const [key, detail] of snapshot) {
    if (detail) queryClient.setQueryData(key, detail)
  }
}

function setDetails(
  queryClient: QueryClient,
  keys: DetailKey[],
  detail: FaceMatchingIdentityDetail
) {
  for (const key of keys) queryClient.setQueryData(key, detail)
}

/**
 * Take *faceId* off every cached detail of the identity, and one pending
 * suggestion off its row of the identity list, before the server answers — a
 * decision and a merge both end with the candidate gone.
 */
async function takeCandidateOff(
  queryClient: QueryClient,
  identity: { caseId: string; personId: string | null; keys: DetailKey[] },
  faceId: string
): Promise<CandidateSnapshot> {
  const { caseId, personId, keys } = identity
  const previousDetails = await snapshotDetails(queryClient, keys)
  const previousList = queryClient.getQueryData<FaceMatchingIdentityList>(
    faceMatchingKeys.identities(caseId)
  )
  // The candidate sits in whichever cached detail it was acted on from.
  const suggestedMatch =
    previousDetails
      .map(([, detail]) => detail?.candidates.find(c => c.face_id === faceId))
      .find(candidate => candidate !== undefined)?.suggested_match ?? false
  for (const [key, detail] of previousDetails) {
    if (detail) queryClient.setQueryData(key, withoutCandidate(detail, faceId))
  }
  if (previousList && personId) {
    queryClient.setQueryData<FaceMatchingIdentityList>(faceMatchingKeys.identities(caseId), {
      identities: withoutPending(previousList.identities, personId, suggestedMatch),
    })
  }
  return { previousDetails, previousList }
}

/** Put back what `takeCandidateOff` replaced: the server refused. */
function restoreCandidate(queryClient: QueryClient, caseId: string, snapshot?: CandidateSnapshot) {
  restoreDetails(queryClient, snapshot?.previousDetails)
  if (snapshot?.previousList) {
    queryClient.setQueryData(faceMatchingKeys.identities(caseId), snapshot.previousList)
  }
}

/**
 * Confirm or reject a candidate. The row leaves the table optimistically; the
 * response is the refreshed detail, so the table and the counts land with it,
 * and a failure (a 409 among them) rolls the cache back and surfaces the error.
 *
 * *queries* are every detail the open identity is read through — the table's
 * current page and the star's own top-N query — so a decision taken in either
 * place leaves both at once (SC-007).
 */
export function useFaceDecision(
  caseId: string,
  personId: string | null,
  queries: IdentityDetailQuery[]
) {
  const queryClient = useQueryClient()
  const keys = detailKeys(caseId, personId, queries)

  return useMutation({
    mutationFn: ({ faceId, decision }: { faceId: string; decision: FaceDecision }) =>
      faceMatchingApi.decide(caseId, personId as string, faceId, decision),
    onMutate: ({ faceId }) => takeCandidateOff(queryClient, { caseId, personId, keys }, faceId),
    onError: (_error, _variables, snapshot) => restoreCandidate(queryClient, caseId, snapshot),
    // The response is the unfiltered head of the list, which is exactly what the
    // star needs; the table's page is refetched by onSettled.
    onSuccess: detail => setDetails(queryClient, keys, detail),
    // The decision moved other identities' suggestions too, so refetch both.
    onSettled: () => {
      invalidateFaceMatching(queryClient, caseId)
    },
  })
}

/** The suggestion a merge is proposed on (ADR-0042). */
export interface IdentityMergeTarget {
  /** The identity the face is suggested for. */
  personId: string
  /** The suggested face; the identity it is confirmed to is the other one. */
  faceId: string
}

/** A merge as the analyst confirmed it: the suggestion, and who goes and who stays (ADR-0052). */
export type IdentityMergeRequest = IdentityMergeTarget & ConfirmedMerge

/**
 * What every face-match merge is: the request, built as an identity merge so
 * that a view on the merged identity follows the survivor (`useMergedInto`), and
 * the reads a merge leaves stale — it moves far more than face matching.
 */
function identityMergeOptions(queryClient: QueryClient, caseId: string) {
  return {
    ...identityMergeMutation(caseId, (merge: IdentityMergeRequest) =>
      faceMatchingApi.merge(caseId, merge.personId, merge.faceId, merge)
    ),
    onSettled: (
      _detail: FaceMatchingIdentityDetail | undefined,
      error: Error | null,
      { mergedId }: IdentityMergeRequest
    ) => invalidateAfterIdentityMerge(queryClient, caseId, error ? null : mergedId),
  }
}

/**
 * The merge of the open identity with the one an assigned candidate's face is
 * confirmed to (ADR-0042), the way the analyst confirmed it: *personId* stays as
 * the merge is proposed, the face's identity once the two were swapped
 * (ADR-0052). Like a decision, the candidate leaves the star and the table
 * optimistically, a refusal (the 409 or the 404) rolls the cache back, and the
 * response is the survivor's refreshed detail.
 */
export function useIdentityMerge(
  caseId: string,
  personId: string | null,
  queries: IdentityDetailQuery[]
) {
  const queryClient = useQueryClient()
  const keys = detailKeys(caseId, personId, queries)

  return useMutation({
    ...identityMergeOptions(queryClient, caseId),
    onMutate: ({ faceId }) => takeCandidateOff(queryClient, { caseId, personId, keys }, faceId),
    onError: (_error, _merge, snapshot) => restoreCandidate(queryClient, caseId, snapshot),
    // The answer lands under the identity it names. That is the open one as the
    // merge is proposed; swapped, it is the survivor, whose star the view that
    // follows it then shows at once.
    onSuccess: detail =>
      setDetails(queryClient, detailKeys(caseId, detail.person_id, queries), detail),
  })
}

/**
 * The same merge for any suggestion — the document overlay's candidates each
 * name their own identity. Nothing is written optimistically there: the overlay
 * shows the refreshed read, as it does after a decision.
 */
export function useIdentityMergeRequest(caseId: string) {
  return useMutation(identityMergeOptions(useQueryClient(), caseId))
}

/**
 * Toggle a confirmed face as a reference. The recompute runs server-side inside
 * the same request, so the response already carries the new candidate table.
 */
export function useFaceReferenceToggle(
  caseId: string,
  personId: string | null,
  queries: IdentityDetailQuery[]
) {
  const queryClient = useQueryClient()
  const keys = detailKeys(caseId, personId, queries)

  return useMutation({
    mutationFn: ({ faceId, reference }: { faceId: string; reference: boolean }) =>
      faceMatchingApi.setReference(caseId, personId as string, faceId, reference),
    onMutate: async ({ faceId, reference }) => {
      const previousDetails = await snapshotDetails(queryClient, keys)
      for (const [key, detail] of previousDetails) {
        if (detail) queryClient.setQueryData(key, withReference(detail, faceId, reference))
      }
      return { previousDetails }
    },
    onError: (_error, _variables, context) => restoreDetails(queryClient, context?.previousDetails),
    onSuccess: detail => setDetails(queryClient, keys, detail),
    onSettled: () => {
      invalidateFaceMatching(queryClient, caseId)
    },
  })
}
